import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Notifications from '../components/home/notifications/Notifications';
import Providers from '../components/home/providers/Providers';
import Drivers from '../components/home/drivers/Drivers';
import Vehicles from '../components/home/vehicles/Vehicles';
import Provider from '../components/home/providers/Provider';
import Driver from '../components/home/drivers/Driver';
import Vehicle from '../components/home/vehicles/Vehicle';
import User from '../components/home/users/User';
import OnboardUser from '../components/home/users/OnboardUser';
import Trips from '../components/home/trips/Trips';
import About from '../components/home/about/About';
import { useCanOnboardUser } from '../hooks/permissions/useCanOnboardUser';
import { useLoggedInProviderId } from '../hooks/useLoggedInProviderId';
import { useAppSelector } from '../hooks/useAppSelector';
import * as userSlice from '../data/user.slice';
import * as providerSlice from '../data/provider.slice';
import Tier3Provider from '../components/home/providers/Tier3Provider';
import TempNotifications from '../components/home/notifications/TempNotifications';
import Status from '../components/home/status/Status';
import { useIsF1Provider } from '../hooks/permissions/useIsF1Provider';
import { useIsPRUser } from '../hooks/permissions/useIsPRUser';
import { useIsPRAdmin } from '../hooks/permissions/useIsPRAdmin';
import Invoices from '../components/home/invoices/Invoices';

const HomeRouting = () => {
  const [providerId, setProviderId] = React.useState<
    number | null | undefined
  >();

  const navigate = useNavigate();
  const canOnboardUser = useCanOnboardUser();
  const isLoaded = useAppSelector(userSlice.selectLoadedUserInfo);
  const loadingProviderError = useAppSelector(
    providerSlice.selectLoadingProviderError,
  );
  const isF1Provider = useIsF1Provider();
  const isPRUser = useIsPRUser();
  const isPRAdmin = useIsPRAdmin();
  const loggedInProviderId = useLoggedInProviderId();

  React.useEffect(() => {
    if (isLoaded) {
      setProviderId(loggedInProviderId);
    }
  }, [loggedInProviderId, isLoaded]);

  React.useEffect(() => {
    if (loadingProviderError) {
      navigate('/noaccess');
    }
  }, [loadingProviderError]);

  const allRoutes = (
    <Routes>
      {/* <Route path="/">
        <Navigate to="/providers" replace />
      </Route> */}
      <Route path="/" element={<Navigate to="/providers" replace />} />
      <Route path={`/home`} element={<Notifications />} />
      <Route path={`/providers/tier3/:id`} element={<Tier3Provider />} />
      <Route path={`/providers/:id`} element={<Provider />} />
      <Route path={`/providers`} element={<Providers />} />
      <Route path={`/drivers/:id`} element={<Driver />} />
      <Route path={`/drivers`} element={<Drivers provider={null} />} />
      <Route path={`/vehicles/:id`} element={<Vehicle />} />
      <Route path={`/vehicles`} element={<Vehicles />} />
      <Route path={`/users/:id`} element={<User />} />
      {canOnboardUser && <Route path={`/onboard`} element={<OnboardUser />} />}
      /* These next two are temporary so wan show "Benson" and navigate from
      there to /tripPackages */
      {isPRUser && <Route path={`/benson`} element={<Status />} />}
      {isPRUser && <Route path={`/invoices`} element={<Invoices />} />}
      {isPRUser && (
        <Route path={`/tripPackages`} element={<TempNotifications />} />
      )}
      {(isPRUser || isPRAdmin) && (
        <Route path={`/providerview/:id`} element={<Status />} />
      )}
      <Route path={`/about`} element={<About />} />
      <Route path="*" element={<Navigate to={`/`} replace />} />
    </Routes>
  );

  const providerUrl = `/providers/${providerId}`;
  const providerRoutes = (
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route
        path={`/home`}
        element={isF1Provider ? <Status /> : <TempNotifications />}
      />
      <Route path={`/invoices`} element={<Invoices />} />
      <Route path={`/tripPackages`} element={<TempNotifications />} />
      <Route
        path="/providers"
        element={<Navigate to={providerUrl} replace />}
      />
      <Route path={`/providers/:id`} element={<Provider />} />
      <Route path={`/drivers/:id`} element={<Driver />} />
      <Route path={`/vehicles/:id`} element={<Vehicle />} />
      <Route path={`/users/:id`} element={<User />} />
      <Route path={`/trips`} element={<Trips />} />
      <Route path={`/about`} element={<About />} />
      <Route path="*" element={<Navigate to={`/`} replace />} />
    </Routes>
  );

  const routes = providerId ? providerRoutes : allRoutes;

  return providerId === undefined ? null : routes;
};

export default HomeRouting;
